/**
 * 一人称での Entity 設置・破壊。
 *
 * スロットは選択中の操作レシピ（empty / build / tool）。クリックは左右とも use() で、
 * スロットの kind に応じて設置または破壊する。空スロットはハイライトも操作もしない。
 * 発光などの性質は components から決まる。
 */

import type { BlockPose } from '../shared/block-lattice';
import type {
  BraceEntity,
  BraceGeometry,
  ClientMessage,
  ClothBake,
  Entity,
  EntityComponents,
  PlaneGeometry,
  StairGeometry,
  Vec3,
} from '../shared/types';
import { canSimulateCushion } from '../shared/cloth';
import { canRevolve, hasRevolve, setBraceRevolve, type RevolveReject } from '../shared/revolve';
import { resolveObjectDefinition } from '../shared/object-catalog';
import { objectWorldAabb } from '../shared/object-pose';
import { onAimedSurface, placeObject } from '../shared/object-snap';
import {
  addFillHole,
  addFillSideHole,
  addPathHole,
  asFillGeometry,
  asPathGeometry,
  asPlaneGeometry,
  braceAsSlab,
  braceExtrudeOffset,
  bracePolyline,
  braceVertexAt,
  extrudeBraceUp,
  extrudePlaneUp,
  intersectRayPlane,
  isFillBrace,
  isPathBrace,
  nearestBraceSegmentIndex,
  nearestBraceVertexIndex,
  fillSideFace,
  nearestFillFace,
  nearestPathFace,
  nearestPlaneSegmentIndex,
  pathFacePlane,
  planeBasis,
  planeSlab,
  pathLogicalFace,
  rectangleFromThreePoints,
  setBraceExtrude,
  setBraceSegmentArc,
  setPlaneExtrude,
  setPlaneSegmentArc,
  STAIR_MIN_HORIZONTAL,
  stairFromThreePoints,
  translateEntity,
  vec,
  worldToPathUv,
  type PathFace,
} from '../shared/geometry';
import { snapToBrace } from '../shared/brace-snap';
import { resolveOverlappingPlaneThickness } from '../shared/plane-overlap';
import { snapToPathFace, snapToPathGeometry, snapToPlane } from '../shared/plane-snap';
import { pointInPolygon } from '../shared/holes';
import { SOLID_MATERIALS, TEXTURED_MATERIALS } from '../shared/materials';
import {
  blockAabb,
  boundsOf,
  entityCollisionBoxes,
  entityOverlapsPose,
  poseVolume,
  RANGE_DESTROY_SNAP,
  rangeDestroyBox,
  unionBounds,
} from '../shared/physics';
import {
  clipboardYawFromLook,
  copyRegionBox,
  copyRegionVolume,
  entityHitsCopyBox,
  clipboardItemToEntity,
  parseClipboardItems,
  placeClipboardItems,
  toClipboardItems,
  type ClipboardItem,
} from '../shared/copy-paste';
import { snapOnGrid, snapOnYaw, yawFromWorldXZ } from '../shared/view-grid';
import {
  DEFAULT_LIGHT_COLOR,
  DEFAULT_LIGHT_INTENSITY,
  DEFAULT_LIGHT_RANGE,
  MAX_HOLES,
  MAX_BRACE_POINTS,
  MAX_ENTITY_LIGHTS,
  MAX_LIGHT_INTENSITY,
  MAX_LIGHT_RANGE,
  MAX_PLANE_POINTS,
  computePolygonNormal,
  isPlanar,
} from '../shared/validation';
import type { LocalPlayer } from './player';
import type { PickResult, WorldScene } from './scene';
import { formatLocaleNumber, t } from './i18n';

export type BuildShape = 'brace' | 'plane' | 'polygon' | 'stair';

function slotShapeForEntity(entity: Entity, current?: BuildShape): BuildShape {
  if (entity.type === 'object') return current ?? 'plane';
  if (isFillBrace(entity)) return current === 'polygon' ? 'polygon' : 'plane';
  return entity.type;
}

export const BUILD_SHAPES: BuildShape[] = ['brace', 'plane', 'polygon', 'stair'];

export const SHAPE_LABELS: Record<BuildShape, string> = {
  brace: 'ブレース',
  plane: 'プレーン',
  polygon: 'ポリゴン',
  stair: '階段',
};

export type BuildSpec =
  | {
      kind: 'brace';
      thickness: number;
      rotation: number;
    }
  | {
      kind: 'plane';
      thickness: number;
    }
  | {
      kind: 'polygon';
      thickness: number;
    }
  | {
      kind: 'stair';
    };

/** スロットに載せられるツール。 */
export type ToolId =
  | 'destroy'
  | 'range-destroy'
  | 'copy-paste'
  | 'brace-bend'
  | 'extrude'
  | 'extrude-up'
  | 'extrude-down'
  | 'hole'
  | 'cloth-sim'
  | 'revolve';

export const TOOL_LABELS: Record<ToolId, string> = {
  destroy: '破壊',
  'range-destroy': '範囲破壊',
  'copy-paste': 'コピペ',
  'brace-bend': '曲げ',
  extrude: '押し出し',
  'extrude-up': '上に押し出し',
  'extrude-down': '下に押し出し',
  hole: '穴開け',
  'cloth-sim': 'クロスシミュ',
  revolve: '回転体',
};

/**
 * スロットは選択中の操作レシピ。
 * empty = 何も持たない、build = 設置、tool = 破壊などの操作。placeable も同じ階層へ足せる。
 */
export type SlotConfig =
  | { kind: 'empty' }
  | {
      kind: 'build';
      material: string;
      shape: BuildSpec;
      /** 光源など、作成する Entity へ付ける形状と直交した性質。 */
      components?: EntityComponents;
    }
  | {
      kind: 'placeable';
      definitionId: string;
      material?: string;
      components?: EntityComponents;
    }
  | {
      kind: 'tool';
      tool: ToolId;
    }
  | {
      kind: 'clipboard';
      yaw: number;
      items: ClipboardItem[];
    };

type BuildSlotConfig = Extract<SlotConfig, { kind: 'build' }>;
type PlaceableSlotConfig = Extract<SlotConfig, { kind: 'placeable' }>;
type ClipboardSlotConfig = Extract<SlotConfig, { kind: 'clipboard' }>;

type InteractionState =
  | { kind: 'idle' }
  | { kind: 'range-destroy'; start: Vec3 }
  | { kind: 'copy-paste'; start: Vec3; aimedId: string | null }
  | { kind: 'brace-bend'; entityId: string; segmentIndex: number }
  | { kind: 'extrude'; entityId: string; origin?: Vec3 }
  | { kind: 'brace'; points: Vec3[] }
  | { kind: 'plane'; points: Vec3[] }
  | { kind: 'stair'; points: Vec3[] }
  | { kind: 'hole'; entityId: string; segment?: number; fillSide?: boolean; points: Vec3[] };

/** 手が届く距離（マイクラのクリエイティブと同じくらい） */
export const REACH = 8;
/** ホットバーのスロット数（0 が空、1〜9 が操作） */
export const HOTBAR_SIZE = 10;
/** 何も持たない固定枠。アイテムやツールは置けない。 */
export const EMPTY_SLOT_INDEX = 0;
/** 空スロット追加前の9枠。保存データの先頭に空を足すために使う。 */
const LEGACY_HOTBAR_SIZE = 9;

const THICKNESS_STEP = 0.05;
const MIN_THICKNESS = 0.05;
const MIN_PLANE_THICKNESS = 0;
const MAX_THICKNESS = 4;
/** 範囲破壊の箱体積上限（m³）。誤操作で大量削除しないための安全弁。 */
const MAX_RANGE_DESTROY_VOLUME = 4096;
const RANGE_DESTROY_PREVIEW_COLOR = 0xc45c5c;
const COPY_PASTE_PREVIEW_COLOR = 0x5c9ac4;
/** 階段の3点目。平面上でも見えるよう、段ではなく赤い面で出す。 */
const STAIR_DRAFT_PREVIEW_COLOR = 0xc45c5c;

export interface BuilderOptions {
  scene: WorldScene;
  player: LocalPlayer;
  send: (message: ClientMessage) => boolean;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
  onChange: () => void;
  onSlotsChange?: (slots: SlotConfig[]) => void;
  onSlotChange?: (index: number) => void;
  onOpenClothSim?: (entity: BraceEntity) => void;
  slots?: unknown;
  slot?: number;
}

export class Builder {
  /** ホットバーで選択中のスロット */
  slot = 0;
  /** 直前に選んでいたスロット。Z で戻る */
  previousSlot = 0;

  /** 現在クロスヘアが指している対象 */
  target: PickResult | null = null;

  private interaction: InteractionState = { kind: 'idle' };
  private optimistic = new Set<string>();
  private readonly optimisticPrevious = new Map<string, Entity>();
  private readonly configs: SlotConfig[];

  constructor(private readonly options: BuilderOptions) {
    this.configs = normalizeSlotConfigs(options.slots);
    this.slot = this.slotIndex(options.slot ?? 0);
    this.previousSlot = this.slot;
  }

  private get scene(): WorldScene {
    return this.options.scene;
  }

  get shape(): BuildShape {
    return this.currentBuild?.shape.kind ?? 'plane';
  }

  /** 視点グリッドのスナップ間隔。配置・穴開け・上下押し出しで使う。 */
  get snap(): number {
    return this.scene.viewGrid.current.snap;
  }

  get material(): string {
    if (this.currentConfig.kind === 'placeable') {
      return (
        this.currentConfig.material ??
        resolveObjectDefinition(this.currentConfig.definitionId).material ??
        'default'
      );
    }
    return this.currentBuild?.material ?? 'default';
  }

  get pendingPoints(): readonly Vec3[] {
    switch (this.interaction.kind) {
      case 'idle':
        return [];
      case 'range-destroy':
        return [this.interaction.start];
      case 'copy-paste':
        return [this.interaction.start];
      case 'brace-bend':
        return this.braceBendSegmentPoints(
          this.interaction.entityId,
          this.interaction.segmentIndex
        );
      case 'extrude':
        return this.interaction.origin ? [this.interaction.origin] : [];
      case 'brace':
        return this.interaction.points;
      case 'plane':
        return this.interaction.points;
      case 'stair':
        return this.interaction.points;
      case 'hole':
        return this.interaction.points;
    }
  }

  get slotConfigs(): SlotConfig[] {
    return this.configs.map(cloneSlotConfig);
  }

  getSlotConfig(index: number): SlotConfig {
    return cloneSlotConfig(this.configs[this.slotIndex(index)]);
  }

  private get currentConfig(): SlotConfig {
    return this.configs[this.slot];
  }

  private get currentBuild(): BuildSlotConfig | null {
    return this.currentConfig.kind === 'build' ? this.currentConfig : null;
  }

  private get currentPlaceable(): PlaceableSlotConfig | null {
    return this.currentConfig.kind === 'placeable' ? this.currentConfig : null;
  }

  private get currentClipboard(): ClipboardSlotConfig | null {
    return this.currentConfig.kind === 'clipboard' ? this.currentConfig : null;
  }

  updateTarget(): void {
    this.target = this.scene.pickFromCamera(REACH);
    this.applyHighlight();
    this.refreshPreview();
  }

  private applyHighlight(): void {
    if (this.currentConfig.kind === 'empty') {
      this.scene.setHighlight(null);
      return;
    }
    const destroy = this.currentConfig.kind === 'tool' && this.currentConfig.tool === 'destroy';
    if (
      this.currentConfig.kind === 'tool' &&
      (this.currentConfig.tool === 'brace-bend' || this.currentConfig.tool === 'revolve')
    ) {
      if (this.interaction.kind === 'brace-bend') {
        this.scene.setHighlight(this.interaction.entityId, undefined, this.interaction.segmentIndex);
        return;
      }
      const id = this.target?.entityId;
      const entity = id ? this.scene.getEntity(id) : undefined;
      if (this.target && isFillBrace(entity)) {
        const segmentIndex = nearestPlaneSegmentIndex(entity.geometry, this.target.point);
        if (segmentIndex >= 0) {
          this.scene.setHighlight(entity.id, undefined, segmentIndex);
          return;
        }
      } else if (this.target && isPathBrace(entity)) {
        const segmentIndex = nearestBraceSegmentIndex(entity.geometry, this.target.point);
        if (segmentIndex >= 0) {
          this.scene.setHighlight(entity.id, undefined, segmentIndex);
          return;
        }
      }
    }
    if (
      this.currentConfig.kind === 'tool' &&
      (this.currentConfig.tool === 'extrude' ||
        this.currentConfig.tool === 'extrude-up' ||
        this.currentConfig.tool === 'extrude-down')
    ) {
      if (this.interaction.kind === 'extrude') {
        this.scene.setHighlight(this.interaction.entityId);
        return;
      }
      const id = this.target?.entityId;
      const entity = id ? this.scene.getEntity(id) : undefined;
      if (entity?.type === 'brace') {
        this.scene.setHighlight(entity.id);
        return;
      }
    }
    if (this.currentConfig.kind === 'tool' && this.currentConfig.tool === 'cloth-sim') {
      const id = this.target?.entityId;
      const entity = id ? this.scene.getEntity(id) : undefined;
      if (entity?.type === 'brace' && canSimulateCushion(entity.geometry)) {
        this.scene.setHighlight(entity.id);
        return;
      }
    }
    if (this.currentConfig.kind === 'tool' && this.currentConfig.tool === 'hole') {
      const focus = this.target?.point;
      if (this.interaction.kind === 'hole') {
        const locked = this.scene.getEntity(this.interaction.entityId);
        if (isFillBrace(locked) && this.interaction.fillSide && this.interaction.segment !== undefined) {
          const face = fillSideFace(locked.geometry, this.interaction.segment);
          if (face) {
            this.scene.setCustomPlaneSnapHighlight(pathFacePlane(face), this.braceSnapStep, focus, locked.id);
            return;
          }
        }
        if (isFillBrace(locked)) {
          this.scene.setPlaneSnapHighlight(locked.id, this.braceSnapStep, focus);
          return;
        }
        if (isPathBrace(locked) && this.interaction.segment !== undefined) {
          const face = pathLogicalFace(locked.geometry, this.interaction.segment);
          if (face) {
            this.scene.setCustomPlaneSnapHighlight(pathFacePlane(face), this.braceSnapStep, focus, locked.id);
            return;
          }
        }
      }
      const id = this.target?.entityId;
      const aimed = id ? this.scene.getEntity(id) : undefined;
      if (isFillBrace(aimed) && this.target) {
        const target = nearestFillFace(aimed.geometry, this.target.point);
        if (target.kind === 'side') {
          this.scene.setCustomPlaneSnapHighlight(pathFacePlane(target.face), this.braceSnapStep, focus, aimed.id);
          return;
        }
        this.scene.setPlaneSnapHighlight(aimed.id, this.braceSnapStep, focus);
        return;
      }
      if (isPathBrace(aimed) && this.target) {
        const face = nearestPathFace(aimed.geometry, this.target.point);
        if (face) {
          this.scene.setCustomPlaneSnapHighlight(pathFacePlane(face), this.braceSnapStep, focus, aimed.id);
          return;
        }
      }
    }
    if (this.usesBraceSnap && this.target?.entityId) {
      const aimed = this.scene.getEntity(this.target.entityId);
      if (isFillBrace(aimed)) {
        this.scene.setPlaneSnapHighlight(aimed.id, this.braceSnapStep, this.target.point);
        return;
      }
      if (isPathBrace(aimed)) {
        if (vec.length(braceExtrudeOffset(aimed.geometry)) >= 1e-9 && this.target) {
          const face = nearestPathFace(aimed.geometry, this.target.point);
          if (face) {
            this.scene.setCustomPlaneSnapHighlight(pathFacePlane(face), this.braceSnapStep, this.target.point, aimed.id);
            return;
          }
        }
        this.scene.setBraceSnapHighlight(aimed.id, this.braceSnapStep);
        return;
      }
    }
    this.scene.setHighlight(this.target?.entityId ?? null, this.target?.gridCell, undefined, destroy);
  }

  /** 左右クリック共通。スロットの kind に応じて設置または破壊する。 */
  use(): void {
    const config = this.currentConfig;
    if (config.kind === 'empty') return;
    if (config.kind === 'placeable') {
      this.placeObjectAtTarget(config);
      return;
    }
    if (config.kind === 'clipboard') {
      this.placeClipboard(config);
      return;
    }
    if (config.kind === 'tool') {
      if (config.tool === 'destroy') this.breakTarget();
      else if (config.tool === 'range-destroy') this.toggleRangeDestroy();
      else if (config.tool === 'copy-paste') this.toggleCopyPaste();
      else if (config.tool === 'brace-bend') this.toggleBraceBend();
      else if (config.tool === 'extrude') this.toggleExtrude();
      else if (config.tool === 'extrude-up') this.applyDirectedExtrude(this.snap);
      else if (config.tool === 'extrude-down') this.applyDirectedExtrude(-this.snap);
      else if (config.tool === 'hole') this.addHolePoint();
      else if (config.tool === 'cloth-sim') this.openClothSim();
      else if (config.tool === 'revolve') this.applyRevolve();
      return;
    }
    this.useBuild(config);
  }

  /** 長押し連続操作するか。範囲破壊とブレース折れ線はクリックごとに点を足すのでしない。 */
  get repeatsUseOnHold(): boolean {
    if (this.currentConfig.kind === 'empty') return false;
    if (this.currentConfig.kind === 'placeable') return true;
    if (this.currentConfig.kind === 'clipboard') return true;
    if (this.currentConfig.kind === 'tool') return this.currentConfig.tool === 'destroy';
    const kind = this.currentBuild?.shape.kind;
    if (kind === 'brace' || kind === 'polygon') return false;
    return true;
  }

  private useBuild(config: BuildSlotConfig): void {
    const target = this.target;
    if (config.shape.kind === 'plane' || config.shape.kind === 'polygon') {
      if (!target) return;
      this.addPlanePoint(this.snapPoint(target));
      return;
    }
    if (!target) return;

    switch (config.shape.kind) {
      case 'brace':
        this.addBracePathPoint(this.snapPoint(target));
        return;
      case 'stair':
        this.addStairPoint(this.snapPoint(target));
        return;
    }
  }

  private breakTarget(): void {
    const id = this.target?.entityId;
    if (!id) return;
    this.deleteEntityById(id);
  }

  private deleteEntityById(id: string): boolean {
    if (!this.options.send({ type: 'delete_entity', id })) {
      this.options.onError(t('toast.notConnected'));
      return false;
    }
    this.scene.removeEntity(id);
    this.scene.setHighlight(null);
    this.options.onChange();
    return true;
  }

  /** ホイールクリック: 対象の見た目・性質を現在スロットへ取り込む。 */
  pickMaterial(): void {
    const id = this.target?.entityId;
    if (!id) return;
    const entity = this.scene.getEntity(id);
    if (!entity) return;
    if (entity.type === 'object') {
      const exactObject = this.configs.findIndex(
        (slot) =>
          slot.kind === 'placeable' &&
          slot.definitionId === entity.definitionId &&
          (slot.material ?? resolveObjectDefinition(entity.definitionId).material) === entity.material &&
          componentsEqual(slot.components, entity.components)
      );
      if (exactObject >= 0) {
        this.setSlot(exactObject);
        return;
      }
      if (this.slot === EMPTY_SLOT_INDEX) return;
      this.setSlotConfig(this.slot, createPlaceableSlot(entity.definitionId, entity.material));
      return;
    }
    const exact = this.configs.findIndex(
      (slot) =>
        slot.kind === 'build' &&
        slot.material === entity.material &&
        slot.shape.kind === slotShapeForEntity(entity, this.currentBuild?.shape.kind) &&
        componentsEqual(slot.components, entity.components)
    );
    if (exact >= 0) {
      this.setSlot(exact);
      return;
    }
    if (this.slot === EMPTY_SLOT_INDEX) return;

    const shape = defaultBuildSpec(slotShapeForEntity(entity, this.currentBuild?.shape.kind));
    this.setSlotConfig(this.slot, {
      kind: 'build',
      material: selectableMaterial(entity.material, DEFAULT_HOTBAR_MATERIALS[0].id),
      shape,
      components: cloneComponents(entity.components),
    });
  }

  private get usesBraceSnap(): boolean {
    return (
      Boolean(this.currentBuild) ||
      Boolean(this.currentPlaceable) ||
      Boolean(this.currentClipboard) ||
      (this.currentConfig.kind === 'tool' &&
        (this.currentConfig.tool === 'range-destroy' || this.currentConfig.tool === 'hole'))
    );
  }

  private get braceSnapStep(): number {
    return this.currentConfig.kind === 'tool' && this.currentConfig.tool === 'range-destroy'
      ? RANGE_DESTROY_SNAP
      : this.snap;
  }

  private get viewGridYaw(): number {
    return this.scene.viewGrid.current.yaw;
  }

  private snapToAimedBrace(point: Vec3, entityId: string | null): Vec3 | null {
    if (!entityId) return null;
    const entity = this.scene.getEntity(entityId);
    if (isFillBrace(entity)) return snapToPlane(entity.geometry, point, this.braceSnapStep, this.viewGridYaw);
    if (isPathBrace(entity)) {
      if (vec.length(braceExtrudeOffset(entity.geometry)) >= 1e-9) {
        return snapToPathGeometry(entity.geometry, point, this.braceSnapStep, this.viewGridYaw);
      }
      return snapToBrace(entity.geometry, point, this.braceSnapStep);
    }
    return null;
  }

  /** スナップ格子の水平向き。fill / 押し出しはビューグリッド。細い梁は辺の向き。 */
  private snapLatticeYaw(target: PickResult): number {
    const fallback = this.viewGridYaw;
    if (target.gridCell) return fallback;
    if (!target.entityId) return fallback;
    const entity = this.scene.getEntity(target.entityId);
    if (isFillBrace(entity)) return fallback;
    if (isPathBrace(entity)) {
      if (vec.length(braceExtrudeOffset(entity.geometry)) >= 1e-9) return fallback;
      const index = nearestBraceSegmentIndex(entity.geometry, target.point);
      const { points, closed } = bracePolyline(entity.geometry);
      const count = closed ? points.length : points.length - 1;
      if (index < 0 || index >= count) return fallback;
      const start = points[index];
      const end = points[(index + 1) % points.length];
      const yaw = yawFromWorldXZ(end.x - start.x, end.z - start.z);
      return Math.hypot(end.x - start.x, end.z - start.z) < 1e-9 ? fallback : yaw;
    }
    return fallback;
  }

  private snapPoint(target: PickResult): Vec3 {
    if (target.gridCell) {
      return snapOnGrid(target.point, this.scene.viewGrid.current);
    }
    if (target.entityId) {
      if (this.currentBuild || this.currentPlaceable || this.currentClipboard) {
        const braced = this.snapToAimedBrace(target.point, target.entityId);
        if (braced) return braced;
      }
    }
    return snapOnYaw(target.point, this.scene.viewGrid.current.yaw, this.snap);
  }

  private rangeDestroySnapPoint(target: PickResult): Vec3 {
    const braced = this.snapToAimedBrace(target.point, target.entityId);
    if (braced) return braced;
    return snapOnYaw(target.point, this.scene.viewGrid.current.yaw, RANGE_DESTROY_SNAP);
  }

  /**
   * 範囲破壊の 1 回目で始点、2 回目で終点までのグリッド向き直方体に重なる entity を消す。
   */
  private toggleRangeDestroy(): void {
    const target = this.target;
    if (!target) {
      this.options.onNotice(t('toast.rangeNeedSurface'));
      return;
    }
    const point = this.rangeDestroySnapPoint(target);

    const yaw = this.scene.viewGrid.current.yaw;
    if (this.interaction.kind !== 'range-destroy') {
      this.interaction = { kind: 'range-destroy', start: { ...point } };
      this.refreshPreview();
      this.options.onNotice(t('toast.rangeDestroyFirstPoint'));
      this.options.onChange();
      return;
    }

    const pose = rangeDestroyBox(this.interaction.start, point, yaw);
    if (poseVolume(pose) > MAX_RANGE_DESTROY_VOLUME) {
      this.options.onNotice(
        t('toast.rangeDestroyTooLarge', { max: formatLocaleNumber(MAX_RANGE_DESTROY_VOLUME) })
      );
      return;
    }

    this.interaction = { kind: 'idle' };
    for (const entity of this.rangeDestroyTargets(pose)) {
      if (!this.scene.getEntity(entity.id)) continue;
      if (!this.deleteEntityById(entity.id)) break;
    }
    this.refreshPreview();
    this.options.onChange();
  }

  /**
   * コピペの 1 回目で基準座標、2 回目で箱。同一点なら 1 点目の目線先 1 つ。
   */
  private toggleCopyPaste(): void {
    const target = this.target;
    if (!target) {
      this.options.onNotice(t('toast.rangeNeedSurface'));
      return;
    }
    const point = { ...target.point };
    const yaw = this.scene.viewGrid.current.yaw;

    if (this.interaction.kind !== 'copy-paste') {
      this.interaction = { kind: 'copy-paste', start: { ...point }, aimedId: target.entityId };
      this.refreshPreview();
      this.options.onNotice(t('toast.copyPasteFirstPoint'));
      this.options.onChange();
      return;
    }

    const start = this.interaction.start;
    const aimedId = this.interaction.aimedId;
    const samePoint = this.sameSnapPoint(start, point);
    if (!samePoint && copyRegionVolume(start, point, yaw) > MAX_RANGE_DESTROY_VOLUME) {
      this.options.onNotice(
        t('toast.copyPasteTooLarge', { max: formatLocaleNumber(MAX_RANGE_DESTROY_VOLUME) })
      );
      return;
    }

    const pose = samePoint ? null : copyRegionBox(start, point, yaw);
    const targets = pose ? this.copyPasteTargets(pose) : this.copyPasteAimed(aimedId);
    this.interaction = { kind: 'idle' };
    if (targets.length === 0) {
      this.options.onNotice(t('toast.copyPasteEmpty'));
      this.refreshPreview();
      this.options.onChange();
      return;
    }

    const lookYaw = this.clipboardLookYaw();
    this.setSlotConfig(this.slot, {
      kind: 'clipboard',
      yaw: lookYaw,
      items: toClipboardItems(targets, start, lookYaw),
    });
  }

  private copyPasteAimed(aimedId: string | null): Entity[] {
    if (!aimedId) return [];
    const entity = this.scene.getEntity(aimedId);
    return entity ? [entity] : [];
  }

  private copyPasteTargets(pose: BlockPose): Entity[] {
    const targets: Entity[] = [];
    for (const entity of this.scene.listEntities()) {
      if (!entityHitsCopyBox(entity, pose)) continue;
      targets.push(entity);
    }
    return targets;
  }

  /**
   * 曲げの 1 回目で辺、2 回目で曲がる側の制御点。選んだ辺だけを短い円弧に置き換える。
   */
  private toggleBraceBend(): void {
    if (this.interaction.kind !== 'brace-bend') {
      const id = this.target?.entityId;
      const entity = id ? this.scene.getEntity(id) : undefined;
      if (!this.target || !entity || entity.type !== 'brace') {
        this.options.onNotice(t('toast.braceBendNeedBrace'));
        return;
      }
      if (hasRevolve(entity.geometry)) {
        this.options.onNotice(t('toast.revolveNoBend'));
        return;
      }
      const segmentIndex = isFillBrace(entity)
        ? nearestPlaneSegmentIndex(entity.geometry, this.target.point)
        : isPathBrace(entity)
          ? nearestBraceSegmentIndex(entity.geometry, this.target.point)
          : -1;
      if (segmentIndex < 0) {
        this.options.onNotice(t('toast.braceBendNeedBrace'));
        return;
      }
      this.interaction = { kind: 'brace-bend', entityId: entity.id, segmentIndex };
      this.refreshPreview();
      this.options.onNotice(t('toast.braceBendFirstPoint'));
      this.options.onChange();
      return;
    }

    if (!this.target) return;
    const hint = this.braceBendHint(this.target.point);
    const bent = this.bentBrace(this.interaction.entityId, this.interaction.segmentIndex, hint);
    if (bent === 'degenerate') {
      this.options.onNotice(t('toast.braceBendDegenerate'));
      return;
    }
    if (!bent) return;
    this.interaction = { kind: 'idle' };
    this.update(bent);
  }

  /**
   * 押し出しの 1 回目でプレーンまたはブレース、2 回目で長さ／先。
   * プレーンは法線方向、ブレースは選んだ頂点から hint へのオフセット。
   */
  private toggleExtrude(): void {
    if (this.interaction.kind !== 'extrude') {
      const id = this.target?.entityId;
      const entity = id ? this.scene.getEntity(id) : undefined;
      if (!this.target || !entity || entity.type !== 'brace') {
        this.options.onNotice(t('toast.extrudeNeedTarget'));
        return;
      }
      if (isPathBrace(entity)) {
        const origin = braceVertexAt(
          entity.geometry,
          nearestBraceVertexIndex(entity.geometry, this.target.point)
        );
        if (!origin) {
          this.options.onNotice(t('toast.extrudeNeedTarget'));
          return;
        }
        this.interaction = { kind: 'extrude', entityId: entity.id, origin };
        this.refreshPreview();
        this.options.onNotice(t('toast.extrudeFirstBrace'));
      } else {
        this.interaction = { kind: 'extrude', entityId: entity.id };
        this.refreshPreview();
        this.options.onNotice(t('toast.extrudeFirstPlane'));
      }
      this.options.onChange();
      return;
    }

    if (!this.target) return;
    const hint = this.braceBendHint(this.target.point);
    const extruded = this.extrudedEntity(this.interaction.entityId, hint, this.interaction.origin);
    if (extruded === 'degenerate') {
      this.options.onNotice(t('toast.extrudeDegenerate'));
      return;
    }
    if (!extruded) return;
    this.interaction = { kind: 'idle' };
    this.update(extruded);
  }

  /**
   * プレーンまたは同一平面上のブレースを、その面からプレイヤー側（正）または
   * 反対側（負）へスナップ長だけ押し出す。1 クリックで確定。
   */
  private applyDirectedExtrude(distance: number): void {
    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    if (!this.target || !entity || entity.type !== 'brace') {
      this.options.onNotice(t('toast.extrudeNeedTarget'));
      return;
    }
    const extruded = this.extrudedDirectedEntity(entity, distance);
    if (extruded === 'degenerate') {
      this.options.onNotice(
        distance < 0 ? t('toast.extrudeDownDegenerate') : t('toast.extrudeUpDegenerate')
      );
      return;
    }
    if (!extruded) return;
    this.update(extruded);
  }

  private openClothSim(): void {
    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    if (!this.target || entity?.type !== 'brace') {
      this.options.onNotice(t('toast.clothSimNeedFill'));
      return;
    }
    if (!canSimulateCushion(entity.geometry)) {
      this.options.onNotice(t('toast.clothSimFailed'));
      return;
    }
    this.options.onOpenClothSim?.(entity);
  }

  /** 近い論理辺を軸に 360° 回す。同じ辺なら解除。 */
  private applyRevolve(): void {
    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    if (!this.target || !entity || entity.type !== 'brace') {
      this.options.onNotice(t('toast.revolveNeedEdge'));
      return;
    }
    const segmentIndex = this.aimedBraceSegment(entity);
    if (segmentIndex < 0) {
      this.options.onNotice(t('toast.revolveNeedEdge'));
      return;
    }
    const clearing = entity.geometry.revolve?.axis === segmentIndex;
    const reason = canRevolve(entity.geometry, segmentIndex);
    if (!clearing && reason) {
      this.options.onNotice(revolveRejectToast(reason));
      return;
    }
    const next = setBraceRevolve(entity.geometry, segmentIndex);
    if (!next) {
      this.options.onNotice(t('toast.revolveNeedEdge'));
      return;
    }
    if (this.update({ ...entity, geometry: next }) && clearing) {
      this.options.onNotice(t('toast.revolveCleared'));
    }
  }

  private aimedBraceSegment(entity: Extract<Entity, { type: 'brace' }>): number {
    if (!this.target) return -1;
    if (isFillBrace(entity)) return nearestPlaneSegmentIndex(entity.geometry, this.target.point);
    if (isPathBrace(entity)) return nearestBraceSegmentIndex(entity.geometry, this.target.point);
    return -1;
  }

  applyClothBake(entityId: string, bake: ClothBake): boolean {
    const entity = this.scene.getEntity(entityId);
    if (!entity || entity.type !== 'brace') {
      this.options.onNotice(t('toast.clothSimFailed'));
      return false;
    }
    return this.update({
      ...entity,
      geometry: { ...entity.geometry, cloth: bake },
    });
  }

  private extrudedEntity(
    entityId: string,
    hint: Vec3,
    origin?: Vec3
  ): Entity | 'degenerate' | null {
    const entity = this.scene.getEntity(entityId);
    if (!entity) return null;
    if (isFillBrace(entity)) {
      const geometry = setPlaneExtrude(entity.geometry, hint);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asFillGeometry(geometry) };
    }
    if (isPathBrace(entity) && origin) {
      const geometry = setBraceExtrude(entity.geometry, origin, hint);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asPathGeometry(geometry) };
    }
    return null;
  }

  private extrudedDirectedEntity(
    entity: Entity,
    distance: number
  ): Entity | 'degenerate' | null {
    const toward = this.options.player.eyePosition();
    const view = this.options.player.lookDirection();
    if (isFillBrace(entity)) {
      const geometry = extrudePlaneUp(entity.geometry, toward, view, distance);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asFillGeometry(geometry) };
    }
    if (isPathBrace(entity)) {
      const geometry = extrudeBraceUp(entity.geometry, toward, view, distance);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asPathGeometry(geometry) };
    }
    return null;
  }

  private braceBendHint(point: Vec3): Vec3 {
    return snapOnYaw(point, this.scene.viewGrid.current.yaw, this.snap);
  }

  private braceBendSegmentPoints(entityId: string, segmentIndex: number): Vec3[] {
    const entity = this.scene.getEntity(entityId);
    if (!entity) return [];
    if (isFillBrace(entity)) {
      const { points } = bracePolyline(entity.geometry);
      const n = points.length;
      if (segmentIndex < 0 || segmentIndex >= n) return [];
      return [points[segmentIndex], points[(segmentIndex + 1) % n]];
    }
    if (isPathBrace(entity)) {
      const { points, closed } = bracePolyline(entity.geometry);
      const n = points.length;
      const count = closed ? n : n - 1;
      if (segmentIndex < 0 || segmentIndex >= count) return [];
      return [points[segmentIndex], points[(segmentIndex + 1) % n]];
    }
    return [];
  }

  private bentBrace(
    entityId: string,
    segmentIndex: number,
    hint: Vec3
  ): Entity | 'degenerate' | null {
    const entity = this.scene.getEntity(entityId);
    if (!entity) return null;
    if (isFillBrace(entity)) {
      const ends = this.braceBendSegmentPoints(entityId, segmentIndex);
      if (ends.length < 2) return null;
      const geometry = setPlaneSegmentArc(entity.geometry, segmentIndex, hint);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asFillGeometry(geometry) };
    }
    if (isPathBrace(entity)) {
      const ends = this.braceBendSegmentPoints(entityId, segmentIndex);
      if (ends.length < 2) return null;
      const geometry = setBraceSegmentArc(entity.geometry, segmentIndex, hint);
      if (!geometry) return 'degenerate';
      return { ...entity, geometry: asPathGeometry(geometry) };
    }
    return null;
  }

  private addBracePathPoint(point: Vec3): void {
    const points = this.interaction.kind === 'brace' ? this.interaction.points : [];
    if (points.length >= 3 && this.sameSnapPoint(point, points[0])) {
      this.placeBrace(points, true);
      return;
    }
    const last = points[points.length - 1];
    if (last && this.sameSnapPoint(point, last)) {
      this.commit();
      return;
    }
    if (points.length >= MAX_BRACE_POINTS) {
      this.options.onNotice(t('toast.braceTooManyPoints'));
      return;
    }
    this.interaction = { kind: 'brace', points: [...points, point] };
    this.refreshPreview();
    this.options.onChange();
  }

  private placeBrace(points: Vec3[], closed: boolean): void {
    const shape = this.currentBuild?.shape;
    if (!shape || shape.kind !== 'brace') return;
    this.interaction = { kind: 'idle' };
    this.place({
      ...this.entityBase(),
      type: 'brace',
      geometry: this.braceGeometryFromPoints(points, closed),
    });
  }

  private braceGeometryFromPoints(points: Vec3[], closed: boolean): BraceGeometry {
    const shape = this.currentBuild?.shape;
    const thickness = shape && shape.kind === 'brace' ? shape.thickness : 0.2;
    const rotation = shape && shape.kind === 'brace' ? shape.rotation : 0;
    const geometry: BraceGeometry = {
      start: points[0],
      end: points[points.length - 1],
      thickness,
      rotation,
    };
    if (closed || points.length > 2) {
      geometry.points = points;
      if (closed) geometry.closed = true;
    }
    return asPathGeometry(geometry);
  }

  private sameSnapPoint(a: Vec3, b: Vec3): boolean {
    return vec.length(vec.sub(a, b)) < 1e-6;
  }

  private addStairPoint(point: Vec3): void {
    const points = this.interaction.kind === 'stair' ? this.interaction.points : [];
    const last = points[points.length - 1];
    if (last && this.sameSnapPoint(point, last)) {
      this.options.onNotice(t('toast.samePoint'));
      return;
    }
    if (points.length >= 2) {
      const geometry = stairFromThreePoints(points[0], points[1], point);
      if (!geometry) {
        this.options.onNotice(t('toast.stairNeedHorizontal'));
        return;
      }
      this.placeStair(geometry);
      return;
    }
    if (points.length === 1) {
      const start = points[0];
      if (Math.hypot(point.x - start.x, point.z - start.z) < STAIR_MIN_HORIZONTAL) {
        this.options.onNotice(t('toast.stairNeedHorizontal'));
        return;
      }
    }
    this.interaction = { kind: 'stair', points: [...points, point] };
    this.refreshPreview();
    this.options.onChange();
  }

  private placeStair(geometry: StairGeometry): void {
    const shape = this.currentBuild?.shape;
    if (!shape || shape.kind !== 'stair') return;
    this.interaction = { kind: 'idle' };
    this.place({
      ...this.entityBase(),
      type: 'stair',
      geometry,
    });
  }

  private addPlanePoint(point: Vec3): void {
    if (this.currentBuild?.shape.kind === 'polygon') {
      this.addPlanePathPoint(point);
      return;
    }
    const points = this.interaction.kind === 'plane' ? this.interaction.points : [];
    const last = points[points.length - 1];
    if (last && this.sameSnapPoint(point, last)) return;
    if (points.length >= 2) {
      this.placePlaneFromPoints(points[0], points[1], point);
      return;
    }
    this.interaction = { kind: 'plane', points: [...points, point] };
    this.refreshPreview();
    this.options.onChange();
  }

  private addPlanePathPoint(point: Vec3): void {
    const points = this.interaction.kind === 'plane' ? this.interaction.points : [];
    if (points.length >= 3 && this.sameSnapPoint(point, points[0])) {
      this.placePlanePolygon(points);
      return;
    }
    const last = points[points.length - 1];
    if (last && this.sameSnapPoint(point, last)) {
      this.commit();
      return;
    }
    if (points.length >= MAX_PLANE_POINTS) {
      this.options.onNotice(t('toast.planeTooManyPoints'));
      return;
    }
    if (points.length >= 3 && !isPlanar([...points, point])) {
      this.options.onNotice(t('toast.notPlanar'));
      return;
    }
    this.interaction = { kind: 'plane', points: [...points, point] };
    this.refreshPreview();
    this.options.onChange();
  }

  private nearbyFillPlanes(points: Vec3[]): PlaneGeometry[] {
    const existing: PlaneGeometry[] = [];
    for (const id of this.scene.collision.queryIds(boundsOf(points))) {
      const entity = this.scene.getEntity(id);
      if (!isFillBrace(entity)) continue;
      existing.push(asPlaneGeometry(entity.geometry));
    }
    return existing;
  }

  private resolvePlaceThickness(points: Vec3[], normal: Vec3, thickness: number): number {
    return resolveOverlappingPlaneThickness(points, normal, thickness, this.nearbyFillPlanes(points));
  }

  private placePlaneFromPoints(a: Vec3, b: Vec3, c: Vec3): void {
    const shape = this.currentBuild?.shape;
    if (!shape || shape.kind !== 'plane') return;
    const rectangle = rectangleFromThreePoints(a, b, c);
    if (!rectangle) {
      this.options.onNotice(t('toast.colinear'));
      return;
    }
    this.interaction = { kind: 'idle' };
    this.place({
      ...this.entityBase(),
      type: 'brace',
      geometry: asFillGeometry({
        points: rectangle.points,
        thickness: this.resolvePlaceThickness(rectangle.points, rectangle.normal, shape.thickness),
        normal: rectangle.normal,
      }),
    });
  }

  private placePlanePolygon(points: Vec3[]): void {
    const shape = this.currentBuild?.shape;
    if (!shape || (shape.kind !== 'plane' && shape.kind !== 'polygon')) return;
    if (points.length < 3) {
      this.options.onNotice(t('toast.planeNeedPoints'));
      return;
    }
    const normal = computePolygonNormal(points);
    if (!normal) {
      this.options.onNotice(t('toast.colinear'));
      return;
    }
    if (!isPlanar(points)) {
      this.options.onNotice(t('toast.notPlanar'));
      return;
    }
    this.interaction = { kind: 'idle' };
    this.place({
      ...this.entityBase(),
      type: 'brace',
      geometry: asFillGeometry({
        points,
        thickness: this.resolvePlaceThickness(points, normal, shape.thickness),
        normal,
      }),
    });
  }

  /** 折れ線ブレースとポリゴンは Enter で確定。3点プレーンは何もしない。 */
  commit(): void {
    if (this.currentBuild?.shape.kind === 'polygon' && this.interaction.kind === 'plane') {
      this.placePlanePolygon(this.interaction.points);
      return;
    }
    if (this.currentBuild?.shape.kind !== 'brace' || this.interaction.kind !== 'brace') return;
    if (this.interaction.points.length < 2) {
      this.options.onNotice(t('toast.braceNeedPoints'));
      return;
    }
    this.placeBrace(this.interaction.points, false);
  }

  private holeTargetOk(entity: Entity | undefined): boolean {
    if (isFillBrace(entity)) return true;
    if (!isPathBrace(entity)) return false;
    return vec.length(braceExtrudeOffset(entity.geometry)) >= 1e-9;
  }

  private lockedHoleFace(): PathFace | null {
    if (this.interaction.kind !== 'hole') return null;
    const entity = this.scene.getEntity(this.interaction.entityId);
    if (isFillBrace(entity) && this.interaction.fillSide && this.interaction.segment !== undefined) {
      return fillSideFace(entity.geometry, this.interaction.segment);
    }
    if (isPathBrace(entity) && this.interaction.segment !== undefined) {
      return pathLogicalFace(entity.geometry, this.interaction.segment);
    }
    return null;
  }

  private holeSnapPoint(): Vec3 | null {
    const player = this.options.player;
    const origin = player.eyePosition();
    const direction = player.lookDirection();
    if (this.interaction.kind === 'hole') {
      const entity = this.scene.getEntity(this.interaction.entityId);
      if (isFillBrace(entity) && !this.interaction.fillSide) {
        const hit =
          this.target?.entityId === entity.id
            ? this.target.point
            : (() => {
                const slab = braceAsSlab(entity.geometry);
                const raw = slab.normal ?? computePolygonNormal(slab.points);
                if (!raw) return null;
                return intersectRayPlane(origin, direction, slab.points[0], vec.normalize(raw));
              })();
        if (!hit) return null;
        return snapToPlane(entity.geometry, hit, this.snap, this.viewGridYaw);
      }
      const face = this.lockedHoleFace();
      if (!face) return null;
      const hit =
        this.target?.entityId === this.interaction.entityId
          ? this.target.point
          : intersectRayPlane(origin, direction, face.origin, face.normal);
      if (!hit) return null;
      return snapToPathFace(face, hit, this.snap, this.viewGridYaw);
    }
    if (!this.target?.entityId) return null;
    const entity = this.scene.getEntity(this.target.entityId);
    if (isFillBrace(entity)) {
      const target = nearestFillFace(entity.geometry, this.target.point);
      if (target.kind === 'side') return snapToPathFace(target.face, this.target.point, this.snap, this.viewGridYaw);
      return snapToPlane(entity.geometry, this.target.point, this.snap, this.viewGridYaw);
    }
    if (isPathBrace(entity)) {
      const face = nearestPathFace(entity.geometry, this.target.point);
      if (!face) return null;
      return snapToPathFace(face, this.target.point, this.snap, this.viewGridYaw);
    }
    return null;
  }

  private addHolePoint(): void {
    if (this.interaction.kind !== 'hole') {
      if (!this.target) {
        this.options.onNotice(t('toast.holeNeedTarget'));
        return;
      }
      if (!this.target.entityId) {
        this.options.onNotice(t('toast.holeNeedTarget'));
        return;
      }
      const entity = this.scene.getEntity(this.target.entityId);
      if (!this.holeTargetOk(entity)) {
        this.options.onNotice(t('toast.holeNeedTarget'));
        return;
      }
      const point = this.holeSnapPoint();
      if (!point) {
        this.options.onNotice(t('toast.holeNeedTarget'));
        return;
      }
      let segment: number | undefined;
      let fillSide = false;
      if (isFillBrace(entity)) {
        const target = nearestFillFace(entity.geometry, this.target.point);
        if (target.kind === 'side') {
          fillSide = true;
          segment = target.face.segment;
        }
      } else if (isPathBrace(entity)) {
        const face = nearestPathFace(entity.geometry, this.target.point);
        if (!face) {
          this.options.onNotice(t('toast.holeNeedTarget'));
          return;
        }
        segment = face.segment;
      }
      this.interaction = { kind: 'hole', entityId: entity!.id, segment, fillSide, points: [point] };
      this.refreshPreview();
      this.options.onChange();
      return;
    }

    const point = this.holeSnapPoint();
    if (!point) {
      this.options.onNotice(t('toast.holeOutside'));
      return;
    }
    const points = this.interaction.points;
    if (points.length >= 3 && this.sameSnapPoint(point, points[0])) {
      this.commitHole(points);
      return;
    }
    const last = points[points.length - 1];
    if (last && this.sameSnapPoint(point, last)) return;
    if (points.length >= MAX_PLANE_POINTS) {
      this.options.onNotice(t('toast.holeTooManyPoints'));
      return;
    }
    const entity = this.scene.getEntity(this.interaction.entityId);
    if (isFillBrace(entity) && this.interaction.fillSide && this.interaction.segment !== undefined) {
      const face = fillSideFace(entity.geometry, this.interaction.segment);
      if (face) {
        const uv = worldToPathUv(face, point);
        if (uv.u < -1e-6 || uv.u > 1 + 1e-6 || uv.v < -1e-6 || uv.v > 1 + 1e-6) {
          this.options.onNotice(t('toast.holeOutside'));
          return;
        }
      }
    } else if (isFillBrace(entity)) {
      const plane = braceAsSlab(entity.geometry);
      const origin = plane.points[0];
      const raw = plane.normal ?? computePolygonNormal(plane.points);
      if (raw) {
        const { tangent, bitangent } = planeBasis(vec.normalize(raw));
        const slab = planeSlab(plane);
        if (slab) {
          const outer = slab.start.map((p) => {
            const d = vec.sub(p, origin);
            return { x: vec.dot(d, tangent), y: vec.dot(d, bitangent) };
          });
          const trial = [...points, point].map((p) => {
            const d = vec.sub(p, origin);
            return { x: vec.dot(d, tangent), y: vec.dot(d, bitangent) };
          });
          const lastUv = trial[trial.length - 1];
          if (!pointInPolygon(lastUv, outer, true)) {
            this.options.onNotice(t('toast.holeOutside'));
            return;
          }
        }
      }
    } else if (isPathBrace(entity) && this.interaction.segment !== undefined) {
      const face = pathLogicalFace(entity.geometry, this.interaction.segment);
      if (face) {
        const uv = worldToPathUv(face, point);
        if (uv.u < -1e-6 || uv.u > 1 + 1e-6 || uv.v < -1e-6 || uv.v > 1 + 1e-6) {
          this.options.onNotice(t('toast.holeOutside'));
          return;
        }
      }
    }
    this.interaction = { ...this.interaction, points: [...points, point] };
    this.refreshPreview();
    this.options.onChange();
  }

  private commitHole(points: Vec3[]): void {
    if (this.interaction.kind !== 'hole') return;
    if (points.length < 3) {
      this.options.onNotice(t('toast.holeTooFewPoints'));
      return;
    }
    const entity = this.scene.getEntity(this.interaction.entityId);
    if (!entity) return;
    const existingHoles = isFillBrace(entity)
      ? (entity.geometry.capHoles?.length ?? 0) + (entity.geometry.holes?.length ?? 0)
      : isPathBrace(entity)
        ? (entity.geometry.holes?.length ?? 0)
        : 0;
    if (existingHoles >= MAX_HOLES) {
      this.options.onNotice(t('toast.holeTooMany'));
      return;
    }
    let next: Entity | null = null;
    if (isFillBrace(entity) && this.interaction.fillSide && this.interaction.segment !== undefined) {
      const face = fillSideFace(entity.geometry, this.interaction.segment);
      if (!face) {
        this.options.onNotice(t('toast.holeDegenerate'));
        return;
      }
      const uv = points.map((p) => worldToPathUv(face, p));
      const geometry = addFillSideHole(entity.geometry, this.interaction.segment, uv);
      if (!geometry) {
        this.options.onNotice(t('toast.holeDegenerate'));
        return;
      }
      next = { ...entity, geometry };
    } else if (isFillBrace(entity)) {
      const geometry = addFillHole(entity.geometry, points);
      if (!geometry) {
        this.options.onNotice(t('toast.holeDegenerate'));
        return;
      }
      next = { ...entity, geometry };
    } else if (isPathBrace(entity) && this.interaction.segment !== undefined) {
      const face = pathLogicalFace(entity.geometry, this.interaction.segment);
      if (!face) {
        this.options.onNotice(t('toast.holeDegenerate'));
        return;
      }
      const uv = points.map((p) => worldToPathUv(face, p));
      const geometry = addPathHole(entity.geometry, this.interaction.segment, uv);
      if (!geometry) {
        this.options.onNotice(t('toast.holeDegenerate'));
        return;
      }
      next = { ...entity, geometry };
    }
    if (!next) return;
    this.interaction = { kind: 'idle' };
    this.update(next);
  }

  undoPoint(): void {
    if (
      this.interaction.kind === 'range-destroy' ||
      this.interaction.kind === 'copy-paste' ||
      this.interaction.kind === 'brace-bend' ||
      this.interaction.kind === 'extrude'
    ) {
      this.interaction = { kind: 'idle' };
    } else if (this.interaction.kind === 'hole') {
      const points = this.interaction.points.slice(0, -1);
      this.interaction =
        points.length > 0 ? { ...this.interaction, points } : { kind: 'idle' };
    } else if (this.interaction.kind === 'brace') {
      const points = this.interaction.points.slice(0, -1);
      this.interaction = points.length > 0 ? { kind: 'brace', points } : { kind: 'idle' };
    } else if (this.interaction.kind === 'plane') {
      const points = this.interaction.points.slice(0, -1);
      this.interaction = points.length > 0 ? { kind: 'plane', points } : { kind: 'idle' };
    } else if (this.interaction.kind === 'stair') {
      const points = this.interaction.points.slice(0, -1);
      this.interaction = points.length > 0 ? { kind: 'stair', points } : { kind: 'idle' };
    } else {
      return;
    }
    this.refreshPreview();
    this.options.onChange();
  }

  cancel(): void {
    if (this.currentConfig.kind === 'clipboard') {
      this.setSlotConfig(this.slot, createCopyPasteToolSlot());
      return;
    }
    if (this.interaction.kind === 'idle') return;
    this.interaction = { kind: 'idle' };
    this.refreshPreview();
    this.options.onChange();
  }

  setSlot(index: number): void {
    const next = this.slotIndex(index);
    if (next !== this.slot) {
      this.previousSlot = this.slot;
      this.interaction = { kind: 'idle' };
    }
    this.slot = next;
    this.refreshPreview();
    this.options.onSlotChange?.(this.slot);
    this.options.onChange();
  }

  cycleSlot(delta: number): void {
    this.setSlot(this.slot + delta);
  }

  /** ホットバー上の破壊ツールへ切り替える。無いときは false。 */
  selectDestroyTool(): boolean {
    const index = this.configs.findIndex((slot) => slot.kind === 'tool' && slot.tool === 'destroy');
    if (index < 0) return false;
    this.setSlot(index);
    return true;
  }

  restorePreviousSlot(): void {
    this.setSlot(this.previousSlot);
  }

  setSlotConfig(index: number, config: SlotConfig): void {
    const target = this.slotIndex(index);
    if (target === EMPTY_SLOT_INDEX) return;
    const previousKey = slotInteractionKey(this.configs[target]);
    this.configs[target] = normalizeSlotConfig(config, target);
    if (target === this.slot && previousKey !== slotInteractionKey(this.configs[target])) {
      this.interaction = { kind: 'idle' };
    }
    this.refreshPreview();
    this.notifySlotsChanged();
  }

  adjustThickness(direction: number): void {
    const shape = this.currentBuild?.shape;
    if (!shape) return;
    const delta = Math.sign(direction) * THICKNESS_STEP;
    if (shape.kind === 'brace') {
      shape.thickness = clamp(shape.thickness + delta, MIN_THICKNESS, MAX_THICKNESS);
    } else if (shape.kind === 'plane' || shape.kind === 'polygon') {
      shape.thickness = clamp(shape.thickness + delta, MIN_PLANE_THICKNESS, MAX_THICKNESS);
    } else {
      return;
    }
    this.refreshPreview();
    this.notifySlotsChanged();
  }

  shapeSummary(): string {
    const config = this.currentConfig;
    if (config.kind === 'empty') return t('tool.empty');
    if (config.kind === 'placeable') {
      return t('build.objectSummary', { snap: this.snap });
    }
    if (config.kind === 'clipboard') {
      return t('build.clipboardSummary', { snap: this.snap, count: config.items.length });
    }
    if (config.kind === 'tool') {
      if (config.tool === 'hole') {
        return t('build.holeSummary', {
          snap: this.snap,
          count: this.pendingPoints.length,
        });
      }
      if (config.tool === 'extrude-up' || config.tool === 'extrude-down') {
        return t('build.objectSummary', { snap: this.snap });
      }
      return toolLabel(config.tool);
    }
    const shape = config.shape;
    const extras = componentSummary(config.components);
    switch (shape.kind) {
      case 'brace':
        return (
          t('build.braceSummary', {
            thickness: shape.thickness.toFixed(2),
            snap: this.snap,
            count: this.pendingPoints.length,
          }) +
          this.enterToPlaceHint() +
          extras
        );
      case 'plane':
        return (
          t('build.planeSummary', {
            thickness: shape.thickness.toFixed(2),
            snap: this.snap,
            count: this.pendingPoints.length,
            mode: t('build.planeModeRect'),
          }) + extras
        );
      case 'polygon':
        return (
          t('build.polygonSummary', {
            thickness: shape.thickness.toFixed(2),
            snap: this.snap,
            count: this.pendingPoints.length,
          }) +
          this.enterToPlaceHint() +
          extras
        );
      case 'stair':
        return t('build.stairSummary', { snap: this.snap, count: this.pendingPoints.length }) + extras;
    }
  }

  /** 折れ線／ポリゴンのプレビュー中だけ、Enter で確定できることを出す。 */
  private enterToPlaceHint(): string {
    if (this.pendingPoints.length === 0) return '';
    const kind = this.currentBuild?.shape.kind;
    if (kind !== 'brace' && kind !== 'polygon') return '';
    return ` / ${t('build.enterToPlace')}`;
  }

  private entityBase(): Pick<Entity, 'id' | 'material' | 'components'> {
    const build = this.currentBuild;
    if (!build) throw new Error('entityBase requires a build slot');
    return {
      id: crypto.randomUUID(),
      material: build.material,
      components: cloneComponents(build.components),
    };
  }

  private place(entity: Entity): boolean {
    if (!this.options.send({ type: 'place_entity', entity })) {
      this.options.onError(t('toast.notConnected'));
      return false;
    }
    this.optimistic.add(entity.id);
    this.scene.setEntity(entity);
    this.refreshPreview();
    this.options.onChange();
    return true;
  }

  private update(entity: Entity): boolean {
    if (!this.options.send({ type: 'update_entity', entity })) {
      this.options.onError(t('toast.notConnected'));
      return false;
    }
    const previous = this.scene.getEntity(entity.id);
    if (previous) this.optimisticPrevious.set(entity.id, structuredClone(previous));
    this.optimistic.add(entity.id);
    this.scene.setEntity(entity);
    this.refreshPreview();
    this.options.onChange();
    return true;
  }

  rollback(refId: string | undefined): void {
    if (!refId) return;
    const previous = this.optimisticPrevious.get(refId);
    if (previous) {
      this.optimisticPrevious.delete(refId);
      this.optimistic.delete(refId);
      this.scene.setEntity(previous);
      this.options.onChange();
      return;
    }
    if (this.optimistic.delete(refId)) {
      this.scene.removeEntity(refId);
      this.options.onChange();
    }
  }

  confirm(id: string): void {
    this.optimistic.delete(id);
    this.optimisticPrevious.delete(id);
  }

  private refreshPreview(): void {
    const config = this.currentConfig;
    if (config.kind === 'tool' && config.tool === 'range-destroy') {
      this.scene.setGuide([], null);
      this.refreshRangeDestroyPreview();
      return;
    }
    if (config.kind === 'tool' && config.tool === 'copy-paste') {
      this.refreshCopyPastePreview();
      return;
    }
    if (config.kind === 'clipboard') {
      this.scene.setGuide([], null);
      this.scene.setPreview(null, undefined, undefined, this.previewClipboard(config));
      return;
    }
    if (config.kind === 'tool' && config.tool === 'brace-bend') {
      this.refreshBraceBendPreview();
      return;
    }
    if (config.kind === 'tool' && config.tool === 'extrude') {
      this.refreshExtrudePreview();
      return;
    }
    if (config.kind === 'tool' && config.tool === 'extrude-up') {
      this.refreshDirectedExtrudePreview(this.snap);
      return;
    }
    if (config.kind === 'tool' && config.tool === 'extrude-down') {
      this.refreshDirectedExtrudePreview(-this.snap);
      return;
    }
    if (config.kind === 'tool' && config.tool === 'hole') {
      this.refreshHolePreview();
      return;
    }
    if (config.kind === 'tool' && config.tool === 'revolve') {
      this.refreshRevolvePreview();
      return;
    }

    const placeable = this.currentPlaceable;
    if (placeable) {
      this.scene.setGuide([], null);
      this.scene.setPreview(this.previewPlaceable(placeable));
      return;
    }

    const build = this.currentBuild;
    if (!build) {
      this.scene.setGuide([], null);
      this.scene.setPreview(null);
      return;
    }

    const target = this.target;
    const cursor = target ? this.snapPoint(target) : null;
    const shape = build.shape;

    if (shape.kind === 'brace') {
      const pending = this.interaction.kind === 'brace' ? this.interaction.points : [];
      const closing = Boolean(
        cursor && pending.length >= 3 && this.sameSnapPoint(cursor, pending[0])
      );
      this.scene.setGuide(pending, closing ? null : cursor, closing, shape.thickness);
      const last = pending[pending.length - 1];
      const previewPoints =
        closing || !cursor
          ? pending
          : last && this.sameSnapPoint(cursor, last)
            ? pending
            : [...pending, cursor];
      const valid = previewPoints.length >= 2 && (!closing || previewPoints.length >= 3);
      this.scene.setPreview(
        valid
          ? {
              ...this.previewBase(),
              type: 'brace',
              geometry: this.braceGeometryFromPoints(previewPoints, closing),
            }
          : null
      );
      return;
    }

    if (shape.kind === 'stair') {
      const pending = this.interaction.kind === 'stair' ? this.interaction.points : [];
      this.scene.setGuide(pending, cursor);
      if (pending.length >= 2) {
        const rectangle =
          cursor && !this.sameSnapPoint(cursor, pending[1])
            ? rectangleFromThreePoints(pending[0], pending[1], cursor)
            : null;
        if (rectangle) {
          this.scene.setPreview(
            {
              ...this.previewBase(),
              type: 'brace',
              geometry: asFillGeometry({
                points: rectangle.points,
                thickness: 0,
                normal: rectangle.normal,
              }),
            },
            undefined,
            STAIR_DRAFT_PREVIEW_COLOR
          );
          return;
        }
      }
      const bracePoints =
        pending.length >= 1 && cursor && !this.sameSnapPoint(pending[pending.length - 1], cursor)
          ? [...pending, cursor]
          : pending;
      this.scene.setPreview(
        bracePoints.length >= 2
          ? {
              ...this.previewBase(),
              type: 'brace',
              geometry: this.braceGeometryFromPoints(bracePoints.slice(0, 2), false),
            }
          : null
      );
      return;
    }

    const pending = this.interaction.kind === 'plane' ? this.interaction.points : [];
    const planeCursor = target ? this.snapPoint(target) : null;
    if (shape.kind === 'polygon') {
      const closing = Boolean(
        planeCursor && pending.length >= 3 && this.sameSnapPoint(planeCursor, pending[0])
      );
      this.scene.setGuide(pending, closing ? null : planeCursor, closing);
      const last = pending[pending.length - 1];
      const previewPoints =
        closing || !planeCursor
          ? pending
          : last && this.sameSnapPoint(planeCursor, last)
            ? pending
            : [...pending, planeCursor];
      const normal = previewPoints.length >= 3 ? computePolygonNormal(previewPoints) : null;
      this.scene.setPreview(
        normal && isPlanar(previewPoints)
          ? {
              ...this.previewBase(),
              type: 'brace' as const,
              geometry: asFillGeometry({
                points: previewPoints,
                thickness: this.resolvePlaceThickness(previewPoints, normal, shape.thickness),
                normal,
              }),
            }
          : null
      );
      return;
    }
    this.scene.setGuide([...pending], planeCursor);
    const rectangle =
      pending.length >= 2 && planeCursor
        ? rectangleFromThreePoints(pending[0], pending[1], planeCursor)
        : null;
    this.scene.setPreview(
      rectangle
        ? {
            ...this.previewBase(),
            type: 'brace' as const,
            geometry: asFillGeometry({
              points: rectangle.points,
              thickness: this.resolvePlaceThickness(
                rectangle.points,
                rectangle.normal,
                shape.thickness
              ),
              normal: rectangle.normal,
            }),
          }
        : null
    );
  }

  private refreshRangeDestroyPreview(): void {
    const start = this.interaction.kind === 'range-destroy' ? this.interaction.start : null;
    const end = this.target ? this.rangeDestroySnapPoint(this.target) : start;
    if (!start || !end) {
      this.scene.setPreview(null);
      return;
    }
    const pose = rangeDestroyBox(start, end, this.scene.viewGrid.current.yaw);
    this.scene.setPreview(
      null,
      pose,
      RANGE_DESTROY_PREVIEW_COLOR,
      this.rangeDestroyTargets(pose),
      'frame'
    );
  }

  private rangeDestroyTargets(pose: BlockPose): Entity[] {
    const targets: Entity[] = [];
    for (const id of this.scene.collision.queryIds(blockAabb(pose))) {
      const entity = this.scene.getEntity(id);
      if (!entity || !entityOverlapsPose(entity, pose)) continue;
      targets.push(entity);
    }
    return targets;
  }

  private refreshCopyPastePreview(): void {
    const start = this.interaction.kind === 'copy-paste' ? this.interaction.start : null;
    const end = this.target ? { ...this.target.point } : start;
    this.scene.setGuide(start ? [start] : [], end && start && !this.sameSnapPoint(start, end) ? end : null);
    if (!start || !end || this.sameSnapPoint(start, end)) {
      this.scene.setPreview(null);
      return;
    }
    const pose = copyRegionBox(start, end, this.scene.viewGrid.current.yaw);
    this.scene.setPreview(
      null,
      pose,
      COPY_PASTE_PREVIEW_COLOR,
      this.copyPasteTargets(pose),
      'frame'
    );
  }

  private refreshBraceBendPreview(): void {
    if (this.interaction.kind !== 'brace-bend') {
      this.scene.setGuide([], null);
      this.scene.setPreview(null);
      return;
    }
    const ends = this.braceBendSegmentPoints(this.interaction.entityId, this.interaction.segmentIndex);
    const hint = this.target ? this.braceBendHint(this.target.point) : null;
    this.scene.setGuide(ends, hint);
    if (!hint) {
      this.scene.setPreview(null);
      return;
    }
    const bent = this.bentBrace(this.interaction.entityId, this.interaction.segmentIndex, hint);
    this.scene.setPreview(bent && typeof bent === 'object' ? { ...bent, id: 'preview' } : null);
  }

  private refreshRevolvePreview(): void {
    this.scene.setGuide([], null);
    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    if (!this.target || !entity || entity.type !== 'brace') {
      this.scene.setPreview(null);
      return;
    }
    const segmentIndex = this.aimedBraceSegment(entity);
    if (segmentIndex < 0) {
      this.scene.setPreview(null);
      return;
    }
    const next = setBraceRevolve(entity.geometry, segmentIndex);
    this.scene.setPreview(next ? { ...entity, geometry: next, id: 'preview' } : null);
  }

  private refreshExtrudePreview(): void {
    if (this.interaction.kind === 'extrude') {
      const hint = this.target ? this.braceBendHint(this.target.point) : null;
      this.scene.setGuide(this.interaction.origin ? [this.interaction.origin] : [], hint);
      if (!hint) {
        this.scene.setPreview(null);
        return;
      }
      const extruded = this.extrudedEntity(this.interaction.entityId, hint, this.interaction.origin);
      this.scene.setPreview(extruded && typeof extruded === 'object' ? { ...extruded, id: 'preview' } : null);
      return;
    }

    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    if (isPathBrace(entity) && this.target) {
      const origin = braceVertexAt(
        entity.geometry,
        nearestBraceVertexIndex(entity.geometry, this.target.point)
      );
      this.scene.setGuide(origin ? [origin] : [], null);
    } else {
      this.scene.setGuide([], null);
    }
    this.scene.setPreview(null);
  }

  private refreshDirectedExtrudePreview(distance: number): void {
    const id = this.target?.entityId;
    const entity = id ? this.scene.getEntity(id) : undefined;
    this.scene.setGuide([], null);
    if (!entity || entity.type !== 'brace') {
      this.scene.setPreview(null);
      return;
    }
    const extruded = this.extrudedDirectedEntity(entity, distance);
    if (!extruded || typeof extruded !== 'object') {
      this.scene.setPreview(null);
      return;
    }
    const preview = { ...extruded, id: 'preview' };
    this.scene.setPreview(
      distance < 0 ? translateEntity(preview, { x: 0, y: 0.01, z: 0 }) : preview
    );
  }

  private refreshHolePreview(): void {
    const pending = this.interaction.kind === 'hole' ? this.interaction.points : [];
    const cursor = this.holeSnapPoint();
    const closing = Boolean(
      cursor && pending.length >= 3 && this.sameSnapPoint(cursor, pending[0])
    );
    this.scene.setGuide(pending, closing ? null : cursor, closing);

    const last = pending[pending.length - 1];
    const previewPoints =
      closing || !cursor
        ? pending
        : last && this.sameSnapPoint(cursor, last)
          ? pending
          : [...pending, cursor];
    if (previewPoints.length < 3) {
      this.scene.setPreview(null);
      return;
    }

    const entityId =
      this.interaction.kind === 'hole' ? this.interaction.entityId : this.target?.entityId;
    const entity = entityId ? this.scene.getEntity(entityId) : undefined;
    if (!entity) {
      this.scene.setPreview(null);
      return;
    }

    let next: Entity | null = null;
    if (isFillBrace(entity) && this.interaction.kind === 'hole' && this.interaction.fillSide) {
      const segment = this.interaction.segment;
      if (segment !== undefined) {
        const face = fillSideFace(entity.geometry, segment);
        if (face) {
          const uv = previewPoints.map((p) => worldToPathUv(face, p));
          const geometry = addFillSideHole(entity.geometry, segment, uv);
          if (geometry) next = { ...entity, geometry };
        }
      }
    } else if (isFillBrace(entity)) {
      const geometry = addFillHole(entity.geometry, previewPoints);
      if (geometry) next = { ...entity, geometry };
    } else if (isPathBrace(entity)) {
      const segment =
        this.interaction.kind === 'hole'
          ? this.interaction.segment
          : nearestPathFace(entity.geometry, previewPoints[0])?.segment;
      if (segment !== undefined) {
        const face = pathLogicalFace(entity.geometry, segment);
        if (face) {
          const uv = previewPoints.map((p) => worldToPathUv(face, p));
          const geometry = addPathHole(entity.geometry, segment, uv);
          if (geometry) next = { ...entity, geometry };
        }
      }
    }
    this.scene.setPreview(next ? { ...next, id: 'preview' } : null);
  }

  private placeObjectAtTarget(config: PlaceableSlotConfig): void {
    const entity = this.previewPlaceable(config);
    if (!entity) return;
    this.place({ ...entity, id: crypto.randomUUID() });
  }

  private placeClipboard(config: ClipboardSlotConfig): void {
    const entities = this.previewClipboard(config);
    if (entities.length === 0) return;
    for (const entity of entities) {
      if (!this.place({ ...entity, id: crypto.randomUUID() })) break;
    }
  }

  private previewClipboard(config: ClipboardSlotConfig): Entity[] {
    const target = this.target;
    if (!target) return [];
    const hit = onAimedSurface(this.snapPoint(target), target.point, target.normal);
    return placeClipboardItems({
      items: config.items,
      hit,
      yaw: this.clipboardLookYaw(),
      normal: target.normal,
      cameraY: this.options.player.eyePosition().y,
    }).map((item, index) => clipboardItemToEntity(item, `preview-${index}`));
  }

  private clipboardLookYaw(): number {
    return clipboardYawFromLook(this.options.player.lookDirection());
  }

  private previewPlaceable(config: PlaceableSlotConfig): Entity | null {
    const target = this.target;
    if (!target) return null;
    const definition = resolveObjectDefinition(config.definitionId);
    const hit = onAimedSurface(this.snapPoint(target), target.point, target.normal);
    const look = this.options.player.lookDirection();
    return {
      id: 'preview',
      type: 'object',
      definitionId: config.definitionId,
      material: config.material ?? definition.material ?? 'default',
      components: cloneComponents(config.components ?? definition.components),
      geometry: placeObject({
        definition,
        hit,
        normal: target.normal,
        look,
        cameraY: this.options.player.eyePosition().y,
        snapYaw: this.snapLatticeYaw(target),
      }),
    };
  }

  private previewBase(): Pick<Entity, 'id' | 'material' | 'components'> {
    const build = this.currentBuild;
    if (!build) throw new Error('previewBase requires a build slot');
    return {
      id: 'preview',
      material: build.material,
      components: cloneComponents(build.components),
    };
  }

  targetSummary(): string {
    const target = this.target;
    if (!target) return '—';
    if (target.gridCell) {
      return `グリッド ${target.gridCell.i}, ${target.gridCell.j} / ${target.gridSide === 'below' ? '下面' : '上面'}`;
    }
    if (!target.entityId) return '—';
    const entity = this.scene.getEntity(target.entityId);
    if (!entity) return '—';
    const bounds =
      entity.type === 'object'
        ? objectWorldAabb(
            resolveObjectDefinition(entity.definitionId).bounds,
            entity.geometry.origin,
            entity.geometry.yaw,
            entity.geometry.mount ?? '+y'
          )
        : unionBounds(entityCollisionBoxes(entity));
    const size = bounds
      ? `${(bounds.max.x - bounds.min.x).toFixed(1)}×${(bounds.max.y - bounds.min.y).toFixed(1)}×${(bounds.max.z - bounds.min.z).toFixed(1)}`
      : '';
    return `${slotShapeForEntity(entity)} / ${entity.material} ${size}${debugComponentSummary(entity.components)}`;
  }

  private slotIndex(index: number): number {
    return ((index % HOTBAR_SIZE) + HOTBAR_SIZE) % HOTBAR_SIZE;
  }

  private notifySlotsChanged(): void {
    this.options.onSlotsChange?.(this.slotConfigs);
    this.options.onChange();
  }
}

export function createEmptySlot(): Extract<SlotConfig, { kind: 'empty' }> {
  return { kind: 'empty' };
}

export function createPlaceableSlot(definitionId: string, material?: string): PlaceableSlotConfig {
  const definition = resolveObjectDefinition(definitionId);
  return {
    kind: 'placeable',
    definitionId,
    material: material ?? definition.material,
    components: cloneComponents(definition.components),
  };
}

export function createBuildSlot(material: string, shape: BuildShape): BuildSlotConfig {
  return { kind: 'build', material, shape: defaultBuildSpec(shape) };
}

export function createDestroyToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'destroy' };
}

export function createRangeDestroyToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'range-destroy' };
}

export function createCopyPasteToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'copy-paste' };
}

export function createBraceBendToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'brace-bend' };
}

export function createExtrudeToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'extrude' };
}

export function createExtrudeUpToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'extrude-up' };
}

export function createExtrudeDownToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'extrude-down' };
}

export function createHoleToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'hole' };
}

export function createClothSimToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'cloth-sim' };
}

export function createRevolveToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return { kind: 'tool', tool: 'revolve' };
}

export function createPlaneExtrudeToolSlot(): Extract<SlotConfig, { kind: 'tool' }> {
  return createExtrudeToolSlot();
}

export function toolLabel(tool: ToolId): string {
  if (tool === 'range-destroy') return t('tool.rangeDestroy');
  if (tool === 'copy-paste') return t('tool.copyPaste');
  if (tool === 'brace-bend') return t('tool.braceBend');
  if (tool === 'extrude') return t('tool.extrude');
  if (tool === 'extrude-up') return t('tool.extrudeUp');
  if (tool === 'extrude-down') return t('tool.extrudeDown');
  if (tool === 'hole') return t('tool.hole');
  if (tool === 'cloth-sim') return t('tool.clothSim');
  if (tool === 'revolve') return t('tool.revolve');
  return t('tool.destroy');
}

export function toolSlotLabel(config: Extract<SlotConfig, { kind: 'tool' }>): string {
  return toolLabel(config.tool);
}

/** 形状は通常のビルドと同じ。点光源は material id ではなく明示コンポーネント。 */
export function createLightSlot(shape: BuildShape): BuildSlotConfig {
  return {
    kind: 'build',
    material: 'light',
    shape: defaultBuildSpec(shape),
    components: {
      lights: [
        {
          kind: 'point',
          color: DEFAULT_LIGHT_COLOR,
          intensity: DEFAULT_LIGHT_INTENSITY,
          range: DEFAULT_LIGHT_RANGE,
        },
      ],
    },
  };
}

/** 見た目は water マテリアル側で決める。 */
export function createWaterSlot(shape: BuildShape): BuildSlotConfig {
  return createBuildSlot('water', shape);
}

function defaultBuildSpec(shape: BuildShape): BuildSpec {
  switch (shape) {
    case 'brace':
      return { kind: 'brace', thickness: 0.2, rotation: 0 };
    case 'plane':
      return { kind: 'plane', thickness: 0 };
    case 'polygon':
      return { kind: 'polygon', thickness: 0 };
    case 'stair':
      return { kind: 'stair' };
  }
}

/** 初期ホットバー用。スロット0は空、1は破壊、3は白いタイルの階段、4は照明箱、9は上に押し出し。残りはプレーン。 */
const DEFAULT_HOTBAR_MATERIALS = TEXTURED_MATERIALS.slice(0, HOTBAR_SIZE - 2);

const RETIRED_PALETTE_IDS = new Set(
  SOLID_MATERIALS.filter(
    (entry) => entry.id !== 'light' && entry.id !== 'scaffold' && entry.id !== 'water'
  ).map((entry) => entry.id)
);

function selectableMaterial(id: string, fallbackId: string): string {
  return RETIRED_PALETTE_IDS.has(id) ? fallbackId : id;
}

function defaultSlotConfig(index: number): SlotConfig {
  if (index === 0) return createEmptySlot();
  if (index === 1) return createDestroyToolSlot();
  if (index === 3) return createBuildSlot('base-white-tile', 'stair');
  if (index === 4) return createPlaceableSlot('box-fixture');
  if (index === 9) return createExtrudeUpToolSlot();
  const material = DEFAULT_HOTBAR_MATERIALS[(index - 2) % DEFAULT_HOTBAR_MATERIALS.length];
  return createBuildSlot(material.id, 'plane');
}

/** localStorage / テスト用。不足や不正な枠はデフォルトで埋める。 */
export function normalizeHotbarSlots(value: unknown): SlotConfig[] {
  const raw = Array.isArray(value) ? value : [];
  const entries = raw.length === LEGACY_HOTBAR_SIZE ? [createEmptySlot(), ...raw] : raw;
  return Array.from({ length: HOTBAR_SIZE }, (_, index) =>
    index === EMPTY_SLOT_INDEX ? createEmptySlot() : normalizeSlotConfig(entries[index], index)
  );
}

function normalizeSlotConfigs(value: unknown): SlotConfig[] {
  return normalizeHotbarSlots(value);
}

function normalizeSlotConfig(value: unknown, index: number): SlotConfig {
  const fallback = defaultSlotConfig(index);
  if (!isRecord(value)) return fallback;

  if (value.kind === 'empty') return createEmptySlot();

  if (value.kind === 'placeable') {
    if (typeof value.definitionId !== 'string' || value.definitionId.length === 0) return fallback;
    return createPlaceableSlot(
      value.definitionId,
      typeof value.material === 'string' ? value.material : undefined
    );
  }

  if (value.kind === 'clipboard') {
    const items = parseClipboardItems(value.items);
    if (!items) return fallback;
    return {
      kind: 'clipboard',
      yaw: boundedNumber(value.yaw, 0, -360, 360),
      items,
    };
  }

  if (value.kind === 'tool') {
    if (value.tool === 'destroy') return createDestroyToolSlot();
    if (value.tool === 'range-destroy') return createRangeDestroyToolSlot();
    if (value.tool === 'copy-paste') return createCopyPasteToolSlot();
    if (value.tool === 'brace-bend') return createBraceBendToolSlot();
    if (value.tool === 'extrude' || value.tool === 'plane-extrude') return createExtrudeToolSlot();
    if (value.tool === 'extrude-up') return createExtrudeUpToolSlot();
    if (value.tool === 'extrude-down') return createExtrudeDownToolSlot();
    if (value.tool === 'hole') return createHoleToolSlot();
    if (value.tool === 'cloth-sim') return createClothSimToolSlot();
    if (value.tool === 'revolve') return createRevolveToolSlot();
    return fallback;
  }

  if (value.kind !== 'build' || !isRecord(value.shape)) return fallback;

  const rawMaterial =
    typeof value.material === 'string' && value.material.length > 0 && value.material.length <= 64
      ? value.material
      : fallback.kind === 'build'
        ? fallback.material
        : DEFAULT_HOTBAR_MATERIALS[0].id;
  const material = selectableMaterial(
    rawMaterial,
    fallback.kind === 'build' ? fallback.material : DEFAULT_HOTBAR_MATERIALS[0].id
  );
  const shape = normalizeBuildSpec(value.shape);
  if (!shape) return fallback;

  return {
    kind: 'build',
    material,
    shape,
    components: normalizeComponents(value.components),
  };
}

function normalizeBuildSpec(value: Record<string, unknown>): BuildSpec | null {
  switch (value.kind) {
    case 'block':
      return { kind: 'plane', thickness: 0 };
    case 'brace':
      return {
        kind: 'brace',
        thickness: boundedNumber(
          typeof value.thickness === 'number' ? value.thickness : value.width,
          0.2,
          MIN_THICKNESS,
          MAX_THICKNESS
        ),
        rotation: boundedNumber(value.rotation, 0, -180, 180),
      };
    case 'plane':
      return {
        kind: 'plane',
        thickness: boundedNumber(value.thickness, 0, MIN_PLANE_THICKNESS, MAX_THICKNESS),
      };
    case 'polygon':
      return {
        kind: 'polygon',
        thickness: boundedNumber(value.thickness, 0, MIN_PLANE_THICKNESS, MAX_THICKNESS),
      };
    case 'stair':
      return { kind: 'stair' };
    default:
      return null;
  }
}

function normalizeComponents(value: unknown): EntityComponents | undefined {
  if (!isRecord(value)) return undefined;
  const lights = Array.isArray(value.lights)
    ? value.lights.slice(0, MAX_ENTITY_LIGHTS).flatMap((raw) => {
        if (!isRecord(raw) || raw.kind !== 'point') return [];
        const offset = isVec3(raw.offset) ? { ...raw.offset } : undefined;
        return [
          {
            kind: 'point' as const,
            color: boundedNumber(raw.color, DEFAULT_LIGHT_COLOR, 0, 0xffffff),
            intensity: boundedNumber(raw.intensity, DEFAULT_LIGHT_INTENSITY, 0, MAX_LIGHT_INTENSITY),
            range: boundedNumber(raw.range, DEFAULT_LIGHT_RANGE, 0, MAX_LIGHT_RANGE),
            ...(offset ? { offset } : {}),
          },
        ];
      })
    : [];
  if (lights.length === 0) return undefined;
  return { lights };
}

function cloneSlotConfig(config: SlotConfig): SlotConfig {
  if (config.kind === 'empty') return { kind: 'empty' };
  if (config.kind === 'placeable') {
    return {
      kind: 'placeable',
      definitionId: config.definitionId,
      material: config.material,
      components: cloneComponents(config.components),
    };
  }
  if (config.kind === 'clipboard') {
    const items = parseClipboardItems(config.items);
    return items
      ? { kind: 'clipboard', yaw: config.yaw, items }
      : { kind: 'empty' };
  }
  if (config.kind === 'tool') {
    return { kind: 'tool', tool: config.tool };
  }
  return {
    kind: 'build',
    material: config.material,
    shape: { ...config.shape },
    components: cloneComponents(config.components),
  };
}

function slotInteractionKey(config: SlotConfig): string {
  if (config.kind === 'empty') return 'empty';
  if (config.kind === 'placeable') return `placeable:${config.definitionId}`;
  if (config.kind === 'clipboard') return `clipboard:${config.items.length}`;
  return config.kind === 'tool' ? `tool:${config.tool}` : `build:${config.shape.kind}`;
}

function cloneComponents(components: EntityComponents | undefined): EntityComponents | undefined {
  const lights = components?.lights;
  if (!lights || lights.length === 0) return undefined;
  return {
    lights: lights.map((light) => ({
      ...light,
      offset: light.offset ? { ...light.offset } : undefined,
    })),
  };
}

function componentSummary(components: EntityComponents | undefined): string {
  const parts: string[] = [];
  const lightCount = components?.lights?.length ?? 0;
  if (lightCount > 0) parts.push(t('build.lightCount', { count: lightCount }));
  return parts.length > 0 ? ` / ${parts.join(' / ')}` : '';
}

function debugComponentSummary(components: EntityComponents | undefined): string {
  const parts: string[] = [];
  const lightCount = components?.lights?.length ?? 0;
  if (lightCount > 0) parts.push(`光源 ${lightCount}`);
  return parts.length > 0 ? ` / ${parts.join(' / ')}` : '';
}

function componentsEqual(a: EntityComponents | undefined, b: EntityComponents | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, min, max)
    : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isVec3(value: unknown): value is Vec3 {
  return (
    isRecord(value) &&
    typeof value.x === 'number' &&
    Number.isFinite(value.x) &&
    typeof value.y === 'number' &&
    Number.isFinite(value.y) &&
    typeof value.z === 'number' &&
    Number.isFinite(value.z)
  );
}

function revolveRejectToast(reason: RevolveReject): string {
  if (reason === 'extrude') return t('toast.revolveHasExtrude');
  if (reason === 'holes') return t('toast.revolveHasHoles');
  if (reason === 'cloth') return t('toast.revolveHasCloth');
  if (reason === 'not-planar') return t('toast.revolveNotPlanar');
  if (reason === 'arc-axis') return t('toast.revolveArcAxis');
  return t('toast.revolveNeedEdge');
}
